import { Box, Chip, Typography } from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

export type PhoneNumberPreviewStatus =
    | "ACTIVE"
    | "SETUP_REQUIRED"
    | "PENDING_PORTING"
    | "PENDING_ACTIVATION";

export type PhoneNumberPreviewSource =
    | "PORTING"
    | "BUYING"
    | "PORTED"
    | "PURCHASED";

export interface PhoneNumberPlanPreview {
    id: string;
    planName: string;
    lineName?: string;
    phoneNumber?: string;
    status: PhoneNumberPreviewStatus;
    source?: PhoneNumberPreviewSource;
}

const chipBaseSx = {
    height: 22,
    borderRadius: "999px",
    fontSize: 11,
    fontWeight: 800,
    textTransform: "none",
};

const activeChipSx = {
    ...chipBaseSx,
    bgcolor: "rgba(34,197,94,0.10)",
    color: "#15803d",
    border: "1px solid rgba(34,197,94,0.22)",
};

const neutralChipSx = {
    ...chipBaseSx,
    bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
    color: "var(--fx-surface-textsecondary, #64748B)",
    border: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.08)",
};

const setupRequiredChipSx = {
    ...chipBaseSx,
    bgcolor: "rgba(251,142,0,0.08)",
    color: "#FB8E00",
    border: "1px solid rgba(251,142,0,0.18)",
    "& .MuiChip-icon": {
        color: "#FB8E00",
    },
};

const pendingPortingChipSx = {
    ...chipBaseSx,
    bgcolor: "rgba(251,142,0,0.10)",
    color: "#b45309",
    border: "1px solid rgba(251,142,0,0.22)",
    "& .MuiChip-icon": {
        color: "#b45309",
    },
};

const pendingActivationChipSx = {
    ...chipBaseSx,
    bgcolor: "rgba(59,130,246,0.10)",
    color: "#1d4ed8",
    border: "1px solid rgba(59,130,246,0.18)",
    "& .MuiChip-icon": {
        color: "#1d4ed8",
    },
};

interface PhoneNumberPreviewRowProps {
    item: PhoneNumberPlanPreview;
}

function getStatusChipConfig(status: PhoneNumberPreviewStatus) {
    switch (status) {
        case "ACTIVE":
            return {
                label: "Active",
                sx: activeChipSx,
                icon: null,
            };

        case "PENDING_PORTING":
            return {
                label: "Pending porting",
                sx: pendingPortingChipSx,
                icon: <AccessTimeOutlinedIcon sx={{ fontSize: "14px !important" }} />,
            };

        case "PENDING_ACTIVATION":
            return {
                label: "Pending activation",
                sx: pendingActivationChipSx,
                icon: <AccessTimeOutlinedIcon sx={{ fontSize: "14px !important" }} />,
            };

        case "SETUP_REQUIRED":
        default:
            return {
                label: "Setup required",
                sx: setupRequiredChipSx,
                icon: <WarningAmberOutlinedIcon sx={{ fontSize: "14px !important" }} />,
            };
    }
}

function getSourceLabel(source?: PhoneNumberPreviewSource) {
    if (source === "PORTING" || source === "PORTED") return "Ported";
    if (source === "BUYING" || source === "PURCHASED") return "Purchased";

    return null;
}

export const PhoneNumberPreviewRow = ({ item }: PhoneNumberPreviewRowProps) => {
    const isActive = item.status === "ACTIVE";
    const hasNumber = Boolean(item.phoneNumber);

    const statusChip = getStatusChipConfig(item.status);
    const sourceLabel = getSourceLabel(item.source);

    const title = hasNumber
        ? item.lineName || "My number"
        : item.planName;

    const subtitle = hasNumber
        ? item.phoneNumber
        : "No number assigned";

    return (
        <Box
            sx={{
                px: 1.5,
                py: 1.35,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                borderRadius: "12px",
                "&:hover": {
                    bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.03)",
                },
            }}
        >
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                    sx={{
                        fontWeight: 900,
                        fontSize: 14,
                        color: "var(--fx-surface-text, #0F172A)",
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "var(--fx-surface-textsecondary, #64748B)",
                        mt: 0.35,
                        lineHeight: 1.2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {subtitle}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexShrink: 0,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                }}
            >
                <Chip
                    icon={statusChip.icon ?? undefined}
                    label={statusChip.label}
                    size="small"
                    sx={statusChip.sx}
                />

                {isActive && sourceLabel ? (
                    <Chip
                        label={sourceLabel}
                        size="small"
                        sx={neutralChipSx}
                    />
                ) : null}
            </Box>
        </Box>
    );
};